import { array, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import * as tus from 'tus-js-client';
import { task, waitForProperty } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import cssTransition from 'ember-css-transitions/modifiers/css-transition';
import { t } from 'ember-intl';

import Button from 'umg-ember-backend/components/ui/button';
import ContentWithSidebar from 'umg-ember-backend/components/ui/content-with-sidebar';
import FileUploader from 'umg-ember-backend/components/ui/file-uploader';
import HlsStream from 'umg-ember-backend/components/ui/hls-stream';
import Loader from 'umg-ember-backend/components/ui/loader';
import config from 'umg-ember-backend/config/environment';
import { imageUrl } from 'umg-ember-backend/helpers/image-url';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

export default class AssetToolsPreviewEditorComponent extends Component {
  // Services
  @service currentUser;
  @service fetch;
  @service flashMessages;
  @service store;

  // Defaults
  uploader = null;
  @tracked snapshotTime;
  @tracked isUpdateFinished = true;
  @tracked isUploadOpen = false;
  @tracked isSnapshotDisabled = true;

  // Getter ans setter
  get snapshotName() {
    return `${this.args.asset.id}_${this.snapshotTime}_preview.jpg`;
  }

  // Actions
  registerCanvasElement = (element) => {
    this.canvas = element;
  };

  registerVideoElement = (element) => {
    this.video = element;
  };

  toggleUpload = () => {
    this.isUploadOpen = !this.isUploadOpen;
  };

  updatePreviewFromUpload = (file) => {
    this.updatePreview.perform(this.args.asset, file.uploadPath);
  };

  validateUpload = (file) => {
    if (file.type == 'image/jpeg') {
      return true;
    }

    this.flashMessages.error('asset_tools.preview_editor.invalid_file');
    return false;
  };

  // Tasks
  takeSnapshot = task({ restartable: true }, async () => {
    this.isUpdateFinished = false;
    let context = this.canvas.getContext('2d');
    context.fillRect(0, 0, this.canvas.width, this.canvas.height);
    context.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);

    this.snapshotTime = new Date().getTime();

    this.canvas.toBlob((blob) => {
      this.uploader = new tus.Upload(blob, {
        chunkSize: 10e6,
        endpoint: `${config.API.host}/asset/files`,
        removeFingerprintOnSuccess: true,
        metadata: {
          filename: this.snapshotName,
          filetype: 'image/jpeg',
          userId: this.currentUser.id,
        },
        onError: () => {
          this.flashMessages.warning('asset_tools.preview_editor.upload_error');
        },
        onSuccess: () => {
          let uploadKey = this.uploader.url.split('/').pop();
          let uploadPath = `${uploadKey}/${this.snapshotName}`;

          this.updatePreview.perform(this.args.asset, uploadPath);
        },
      });

      this.uploader.start();
    }, 'image/jpeg');

    await waitForProperty(this, 'isUpdateFinished');
  });

  updatePreview = task({ restartable: true }, async (asset, filePath) => {
    const response = await this.fetch.put(`/assetPreview/${asset.id}`, {
      data: { previewTempName: filePath },
    });

    await this.store.pushPayload(response);

    this.isUploadOpen = false;
    this.isUpdateFinished = true;
    asset.newPreviewTime = new Date().getTime();
  });

  // Template
  <template>
    <ContentWithSidebar
      @sidebarPosition='left'
      @hasSpacing={{true}}
      class={{bem}}
    >
      <:sidebar>
        <div
          class={{bem 'preview' (hash is-loading=this.updatePreview.isRunning)}}
        >
          <Loader @isWaiting={{this.updatePreview.isRunning}} />

          <img
            src={{imageUrl (array @asset '4/480')}}
            class={{bem 'image'}}
            alt={{@asset.headline}}
          />
          <div class={{bem 'label'}}>
            {{t 'asset_tools.preview_editor.current_preview'}}
          </div>
        </div>

        <Button
          @disabled={{this.isSnapshotDisabled}}
          @displayBlock={{true}}
          @icon='snapshot'
          @label={{t 'asset_tools.preview_editor.take_snapshot'}}
          {{on 'click' (perform this.takeSnapshot)}}
        />
        <div class={{bem 'or'}}>
          {{t 'asset_tools.preview_editor.or'}}
        </div>
        <Button
          @displayBlock={{true}}
          @icon='upload'
          @label={{t 'asset_tools.preview_editor.upload'}}
          @style='outline'
          {{on 'click' this.toggleUpload}}
        />
      </:sidebar>
      <:content>
        {{#if this.isUploadOpen}}
          <div
            class={{bem 'uploader'}}
            {{cssTransition
              (bem 'uploader')
              enterClass=styles.uploader--enter
              leaveClass=styles.uploader--leave
              enterToClass=styles.uploader--enter-to
              leaveToClass=styles.uploader--leave-to
            }}
          >
            <FileUploader
              @isMultiple={{false}}
              @onSuccess={{this.updatePreviewFromUpload}}
              @onValidateUpload={{this.validateUpload}}
            />
          </div>
        {{/if}}

        <FileUploader
          @isMultiple={{false}}
          @onSuccess={{this.updatePreviewFromUpload}}
          @onValidateUpload={{this.validateUpload}}
          @overlayMessage={{t 'asset_tools.preview_editor.overlay_message'}}
        >
          <div class={{bem 'video-wrapper'}}>
            <HlsStream
              @asset={{@asset}}
              class={{bem 'video'}}
              crossorigin='anonymous'
              {{didInsert this.registerVideoElement}}
              {{on 'loadeddata' (fn (mut this.isSnapshotDisabled) false)}}
            />
          </div>
          <canvas
            width={{@asset.sourceFormat.width}}
            height={{@asset.sourceFormat.height}}
            class={{bem 'canvas'}}
            {{didInsert this.registerCanvasElement}}
          ></canvas>
        </FileUploader>
      </:content>
    </ContentWithSidebar>
  </template>
}
